import { useEffect } from 'react';
import { getCreateWith } from '../helpers/createWith';
import { hasUnsavedChanges, isSaving, onSaved, saveAndContinue, supportsSaveAndContinue } from '../helpers/saveSession';

const MIN_SECONDS = 5;

/**
 * Auto-save (GUI route 16): saves in the background on the interval set in the
 * app's settings, only when something changed. A manual save restarts the
 * timer. It waits for the end of a stroke so the saved image has no half-drawn
 * line, and a failed or skipped save tries again at the next interval.
 */
export default function useAutosave(enabled: boolean) {
	useEffect(() => {
		if (!enabled || !supportsSaveAndContinue()) return;
		let cancelled = false;
		let intervalMs = 0;
		let lastResetAt = Date.now();
		let tick: ReturnType<typeof setInterval> | undefined;

		const unsubscribe = onSaved(() => {
			lastResetAt = Date.now();
		});

		const start = async () => {
			try {
				const settings = await getCreateWith()?.get_settings?.();
				const seconds = Number(settings?.autosave_seconds);
				if (cancelled || !Number.isFinite(seconds) || seconds < MIN_SECONDS) return;
				intervalMs = seconds * 1000;
			} catch (error) {
				console.error('Auto-save settings unavailable:', error);
				return;
			}
			lastResetAt = Date.now();
			tick = setInterval(() => {
				if (Date.now() - lastResetAt < intervalMs || isSaving()) return;
				if (!hasUnsavedChanges()) {
					// Nothing new: start the next interval
					lastResetAt = Date.now();
					return;
				}
				const sketch = window.editSketch;
				if (sketch?.mouseIsPressed || sketch?.touchIsDown) return; // try again next second
				saveAndContinue({ silent: true })
					.catch(error => console.error('Auto-save failed:', error))
					.finally(() => {
						lastResetAt = Date.now();
					});
			}, 1000);
		};
		start();

		return () => {
			cancelled = true;
			if (tick) clearInterval(tick);
			unsubscribe();
		};
	}, [enabled]);
}
